/**
 * UserGuideModal Component
 * Modal with comprehensive user guide for the Agents Flow builder
 */

import React, { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import {
  IconBook2,
  IconChevronDown,
  IconChevronRight,
  IconBulb,
  IconAlertTriangle,
  IconCircleCheck,
  IconArrowRight,
} from '@tabler/icons-react';
import './UserGuideModal.css';

// ===============================================
// SECTION DATA
// ===============================================

const SECTIONS = [
  {
    id: 'what-is',
    title: '1. O que é o Fluxo de Agentes',
    content: (
      <>
        <p>
          Pense no fluxo como um <strong>fluxograma de atendimento</strong>: cada caixinha representa algo que o agente faz
          (responder uma pergunta, coletar um dado, enviar uma mensagem), e as setas dizem para onde ir dependendo do que o cliente disse.
        </p>
        <p>
          Você monta esse fluxograma visualmente no canvas, arrastando e conectando <strong>nós</strong> (as caixinhas). Existem 4 tipos:
        </p>
        <table className="guide-table">
          <thead>
            <tr><th>Tipo</th><th>O que faz</th><th>Exemplo</th></tr>
          </thead>
          <tbody>
            <tr><td><span className="guide-tag guide-tag--green">Início / Fim</span></td><td>Marca onde o fluxo começa e termina</td><td>Nó verde (início) e nó vermelho (fim)</td></tr>
            <tr><td><span className="guide-tag guide-tag--blue">Ação</span></td><td>Executa uma tarefa específica</td><td>Enviar mensagem, coletar campo, chamar API</td></tr>
            <tr><td><span className="guide-tag guide-tag--purple">Capacidade</span></td><td>Habilidade completa com IA</td><td>FAQ, Agendamento, Captura de Lead</td></tr>
            <tr><td><span className="guide-tag guide-tag--amber">Condição</span></td><td>Decide qual caminho seguir</td><td>"O cliente quer agendar?" → Sim / Não</td></tr>
            <tr><td><span className="guide-tag guide-tag--purple">Roteador</span></td><td>Direciona entre múltiplos caminhos</td><td>"O que o cliente quer?" → Agendar / Lead / FAQ</td></tr>
          </tbody>
        </table>
      </>
    ),
  },
  {
    id: 'actions',
    title: '2. Ações',
    content: (
      <>
        <p>As ações são tarefas simples e diretas. Elas fazem exatamente o que você configurou.</p>
        <table className="guide-table">
          <thead><tr><th>Ação</th><th>O que faz</th></tr></thead>
          <tbody>
            <tr><td><strong>Mensagem</strong></td><td>Envia uma mensagem de texto fixa para o cliente</td></tr>
            <tr><td><strong>Resposta com IA</strong></td><td>Gera uma resposta usando IA (com base no contexto da conversa)</td></tr>
            <tr><td><strong>Coletar dado</strong></td><td>Pede e salva um dado específico (nome, CPF, email, etc.)</td></tr>
            <tr><td><strong>API</strong></td><td>Faz uma chamada HTTP para um sistema externo</td></tr>
            <tr><td><strong>Variável</strong></td><td>Define o valor de uma variável para uso posterior</td></tr>
            <tr><td><strong>WhatsApp / Email</strong></td><td>Envia notificações ou confirmações</td></tr>
            <tr><td><strong>Enviar Mídia</strong></td><td>Envia imagem, vídeo ou documento da galeria</td></tr>
          </tbody>
        </table>

        <div className="guide-callout guide-callout--info">
          <IconBulb size={16} />
          <div>
            <strong>Aguardar resposta:</strong> Algumas ações têm a opção "Aguardar resposta". Quando ativada,
            o agente envia a mensagem e <strong>para o fluxo ali</strong>, esperando o cliente responder.
            Ative quando precisa da resposta do cliente (ex: "Qual seu nome?"). Desative para mensagens informativas.
          </div>
        </div>

        <h4>Variáveis em mensagens</h4>
        <p>
          Use <code>{'{{nome_da_variavel}}'}</code> dentro de mensagens para inserir dados coletados.
        </p>
        <div className="guide-code">
          "Olá {'{{nome}}'}, seu agendamento foi confirmado para {'{{data}}'}!"
        </div>

        <h4>Validações no Coletar dado</h4>
        <p>Ao coletar um campo, você pode escolher validações:</p>
        <ul>
          <li><strong>Nenhuma</strong> — aceita qualquer texto</li>
          <li><strong>CPF</strong> — valida se é um CPF correto</li>
          <li><strong>Telefone</strong> — valida formato de telefone</li>
          <li><strong>Email</strong> — valida formato de email</li>
          <li><strong>Opções</strong> — aceita apenas uma das opções listadas (ex: "sim, não, talvez")</li>
        </ul>
        <p>Se a validação falhar, o agente pede novamente automaticamente.</p>
      </>
    ),
  },
  {
    id: 'capabilities',
    title: '3. Capacidades',
    content: (
      <>
        <p>
          As capacidades são <strong>habilidades completas</strong>: o agente usa IA junto com ferramentas específicas
          para conduzir uma conversa sobre aquele assunto.
        </p>
        <table className="guide-table">
          <thead><tr><th>Capacidade</th><th>O que faz</th><th>Tipo</th></tr></thead>
          <tbody>
            <tr><td><strong>FAQ</strong></td><td>Busca na base de conhecimento e responde</td><td><span className="guide-tag guide-tag--slate">Não-interativa</span></td></tr>
            <tr><td><strong>Agendamento</strong></td><td>Agendar, cancelar, reagendar horários</td><td><span className="guide-tag guide-tag--blue">Interativa</span></td></tr>
            <tr><td><strong>Captura de Lead</strong></td><td>Salvar dados, validar e sincronizar com CRM</td><td><span className="guide-tag guide-tag--blue">Interativa</span></td></tr>
            <tr><td><strong>Enviar Mídias</strong></td><td>IA decide quando enviar mídias da galeria</td><td><span className="guide-tag guide-tag--slate">Não-interativa</span></td></tr>
            <tr><td><strong>Análise de Mídia</strong></td><td>Analisar imagens e documentos enviados</td><td><span className="guide-tag guide-tag--slate">Não-interativa</span></td></tr>
          </tbody>
        </table>

        <div className="guide-callout guide-callout--warning">
          <IconAlertTriangle size={16} />
          <div>
            <strong>Interativa vs Não-interativa — importante!</strong><br />
            <strong>Interativa</strong> (Agendamento, Lead): conduz um diálogo de múltiplas mensagens. Quando o cliente responde,
            o fluxo <strong>volta para a mesma capacidade</strong>.<br />
            <strong>Não-interativa</strong> (FAQ): dá uma resposta e <strong>avança para o próximo nó</strong>. O FAQ
            não "lembra" o que respondeu.
          </div>
        </div>

        <div className="guide-callout guide-callout--danger">
          <IconAlertTriangle size={16} />
          <div>
            Se você coloca o FAQ sem nada conectado depois, quando o cliente manda outra pergunta, o fluxo tenta
            ir para "o próximo nó" — mas não tem! Configure sempre o que vem depois do FAQ.
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'conditions',
    title: '4. Condições',
    content: (
      <>
        <p>As condições são <strong>pontos de decisão</strong>: avaliam algo e escolhem um dos caminhos.</p>

        <table className="guide-table">
          <thead><tr><th>Condição</th><th>O que avalia</th><th>Saídas</th></tr></thead>
          <tbody>
            <tr><td><strong>Intenção</strong></td><td>Se o cliente tem uma intenção específica (ex: "quer agendar")</td><td>Sim / Não</td></tr>
            <tr><td><strong>Horário</strong></td><td>Se está dentro do horário de atendimento</td><td>Sim / Não</td></tr>
            <tr><td><strong>Dados</strong></td><td>Se um campo coletado tem um valor específico</td><td>Sim / Não</td></tr>
            <tr><td><strong>Roteador Inteligente</strong></td><td>Qual intenção o cliente tem entre várias opções</td><td>Múltiplas rotas + Fallback</td></tr>
          </tbody>
        </table>

        <div className="guide-callout guide-callout--info">
          <IconBulb size={16} />
          <div>
            <strong>Intenção vs. Roteador:</strong> A condição de Intenção responde "sim ou não" para uma única pergunta.
            O Roteador Inteligente analisa a mensagem e direciona entre <strong>várias capacidades de uma vez</strong>.
            Veja a seção 8 para mais detalhes.
          </div>
        </div>

        <div className="guide-callout guide-callout--danger">
          <IconAlertTriangle size={16} />
          <div>
            <strong>Sempre conecte todas as saídas!</strong> Nas condições simples, conecte os dois lados (Sim e Não).
            No Roteador, conecte todas as rotas <strong>e o Fallback</strong>. Se uma saída ficar sem conexão,
            o cliente fica sem resposta.
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'flow-works',
    title: '5. Como o Fluxo Funciona',
    content: (
      <>
        <h4>Ciclo básico</h4>
        <div className="guide-flow-steps">
          <div className="guide-flow-step">Cliente envia mensagem</div>
          <IconArrowRight size={14} className="guide-flow-arrow" />
          <div className="guide-flow-step">Nó executa</div>
          <IconArrowRight size={14} className="guide-flow-arrow" />
          <div className="guide-flow-step">Condição avalia</div>
          <IconArrowRight size={14} className="guide-flow-arrow" />
          <div className="guide-flow-step">Próximo nó</div>
          <IconArrowRight size={14} className="guide-flow-arrow" />
          <div className="guide-flow-step">Pausa (se precisa resposta)</div>
          <IconArrowRight size={14} className="guide-flow-arrow" />
          <div className="guide-flow-step">Cliente responde → retoma</div>
        </div>

        <h4>O loop do FAQ (padrão recomendado)</h4>
        <div className="guide-code">
{`FAQ → Condição "Quer agendar?"
  ├─ Sim → Agendamento
  └─ Não → Fallback → volta ao FAQ`}
        </div>
        <p>
          Esse loop garante que o cliente pode fazer várias perguntas seguidas. Quando demonstrar interesse,
          o fluxo direciona para a ação correta.
        </p>

        <h4>Fallback automático na capacidade interativa</h4>
        <p>
          Quando uma capacidade interativa (como Agendamento) não consegue responder uma pergunta do cliente,
          o sistema <strong>automaticamente busca na base de conhecimento</strong> e tenta dar uma resposta útil.
        </p>
      </>
    ),
  },
  {
    id: 'rag',
    title: '6. Base de Conhecimento (RAG)',
    content: (
      <>
        <p>
          A base de conhecimento armazena as informações que o agente precisa para responder perguntas:
          catálogos, serviços, políticas da empresa, etc.
        </p>

        <h4>Formatos suportados</h4>
        <table className="guide-table">
          <thead><tr><th>Formato</th><th>Como é processado</th></tr></thead>
          <tbody>
            <tr><td><strong>XLSX, CSV</strong></td><td>Cada linha vira um registro separado e isolado</td></tr>
            <tr><td><strong>PDF</strong></td><td>Texto extraído e dividido em blocos</td></tr>
            <tr><td><strong>TXT, DOCX</strong></td><td>Texto extraído e dividido em blocos</td></tr>
          </tbody>
        </table>

        <div className="guide-callout guide-callout--success">
          <IconCircleCheck size={16} />
          <div>
            <strong>Dica para planilhas:</strong> Coloque TODAS as informações de um item na <strong>mesma linha</strong>.
            Uma entidade por linha (1 produto, 1 serviço). Use colunas descritivas (Marca, Modelo, Preço).
          </div>
        </div>

        <div className="guide-callout guide-callout--warning">
          <IconAlertTriangle size={16} />
          <div>
            <strong>PDFs escaneados</strong> (que são imagens de texto) <strong>não podem ser lidos</strong> pelo sistema.
            Prefira PDFs com texto selecionável.
          </div>
        </div>

        <h4>Exemplo correto de planilha</h4>
        <table className="guide-table guide-table--example">
          <thead><tr><th>Modelo</th><th>Ano</th><th>Preço</th><th>Cor</th><th>Descrição</th></tr></thead>
          <tbody>
            <tr><td>Corolla</td><td>2023</td><td>R$ 120.000</td><td>Prata</td><td>Sedan completo, automático</td></tr>
            <tr><td>Civic</td><td>2024</td><td>R$ 135.000</td><td>Preto</td><td>Sedan esportivo, teto solar</td></tr>
          </tbody>
        </table>
      </>
    ),
  },
  {
    id: 'intent',
    title: '7. Condições de Intenção',
    content: (
      <>
        <p>As condições de intenção usam IA para detectar se o cliente quer fazer algo específico.</p>

        <h4>Como funciona</h4>
        <ol>
          <li>Pega a mensagem do cliente e o histórico recente</li>
          <li>Envia para a IA: "O cliente tem a intenção X?"</li>
          <li>A IA responde com <strong>sim/não</strong> e um nível de <strong>confiança</strong> (0 a 100%)</li>
          <li>Se a confiança ≥ mínimo configurado → caminho "Sim"</li>
        </ol>

        <h4>Como configurar a confiança mínima</h4>
        <table className="guide-table">
          <thead><tr><th>Confiança</th><th>Quando usar</th><th>Exemplo</th></tr></thead>
          <tbody>
            <tr><td><strong>50-60%</strong></td><td>Melhor "chutar que sim"</td><td>Direcionar para FAQ</td></tr>
            <tr><td><strong>70-80%</strong></td><td>Caso geral (recomendado)</td><td>Perguntar se quer agendar</td></tr>
            <tr><td><strong>85-95%</strong></td><td>Ação irreversível</td><td>Confirmar cancelamento</td></tr>
          </tbody>
        </table>

        <h4>Exemplo prático: "O cliente quer agendar?" — Confiança 70%</h4>
        <table className="guide-table">
          <thead><tr><th>Mensagem</th><th>Confiança</th><th>Resultado</th></tr></thead>
          <tbody>
            <tr><td>"Quero marcar uma consulta"</td><td>95%</td><td><span className="guide-tag guide-tag--green">Sim</span></td></tr>
            <tr><td>"Tem horário para sexta?"</td><td>82%</td><td><span className="guide-tag guide-tag--green">Sim</span></td></tr>
            <tr><td>"Como funciona o agendamento?"</td><td>55%</td><td><span className="guide-tag guide-tag--red">Não</span></td></tr>
            <tr><td>"Obrigado, era só isso"</td><td>10%</td><td><span className="guide-tag guide-tag--red">Não</span></td></tr>
          </tbody>
        </table>

        <div className="guide-callout guide-callout--info">
          <IconBulb size={16} />
          <div>
            <strong>Dica:</strong> A IA considera sinônimos, gírias, contexto da conversa e confirmações implícitas.
            "Marcar", "reservar", "pegar um horário" são entendidos como "agendar".
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'intent-router',
    title: '8. Roteador Inteligente',
    content: (
      <>
        <p>
          O <strong>Roteador Inteligente</strong> é uma evolução da condição de intenção. Em vez de perguntar
          "sim ou não" para uma única intenção, ele analisa a mensagem do cliente e direciona automaticamente
          para a <strong>capacidade certa</strong> entre várias opções.
        </p>

        <h4>Condição simples vs. Roteador</h4>
        <table className="guide-table">
          <thead><tr><th></th><th>Condição de Intenção</th><th>Roteador Inteligente</th></tr></thead>
          <tbody>
            <tr><td><strong>Pergunta</strong></td><td>"O cliente quer agendar?" → Sim / Não</td><td>"O que o cliente quer?" → Agendamento / Lead / FAQ / ...</td></tr>
            <tr><td><strong>Saídas</strong></td><td>2 (Sim e Não)</td><td>Quantas você configurar + Fallback</td></tr>
            <tr><td><strong>Quando usar</strong></td><td>Verificar 1 intenção específica</td><td>Direcionar entre múltiplas capacidades</td></tr>
          </tbody>
        </table>

        <h4>Como funciona</h4>
        <ol>
          <li>O cliente envia uma mensagem</li>
          <li>A IA analisa a mensagem e compara com <strong>todas as rotas</strong> configuradas de uma vez</li>
          <li>Cada rota tem uma <strong>descrição semântica</strong> que ajuda a IA a entender quando direcionar para ela</li>
          <li>A IA escolhe a rota com maior confiança</li>
          <li>Se nenhuma rota atingir a confiança mínima, vai para o <strong>Fallback</strong> (geralmente o FAQ)</li>
        </ol>

        <h4>Exemplo visual</h4>
        <div className="guide-code">
{`Saudação → Roteador Inteligente
               ├─ "scheduling"       → Agendamento
               ├─ "data_collection"  → Captura de Lead
               ├─ "media_analysis"   → Análise de Mídia
               └─ Fallback (default) → FAQ`}
        </div>

        <h4>Como configurar</h4>
        <ol>
          <li>Arraste o <strong>"Roteador Inteligente"</strong> da aba Condições para o canvas</li>
          <li>Clique no nó para abrir o painel de configuração</li>
          <li>Clique em <strong>"Adicionar Rota"</strong> para cada capacidade que deseja rotear</li>
          <li>Para cada rota, selecione a capacidade e escreva uma <strong>descrição detalhada</strong> da intenção</li>
          <li>Ajuste a <strong>confiança mínima</strong> (padrão: 60%)</li>
          <li>Conecte cada saída (handle) a um nó de capacidade no canvas</li>
          <li>Conecte a saída <strong>Fallback</strong> (na parte inferior) ao nó que deve tratar mensagens não reconhecidas</li>
        </ol>

        <h4>Descrições semânticas — o segredo da precisão</h4>
        <p>
          A qualidade do roteamento depende diretamente das <strong>descrições</strong> que você escreve para cada rota.
          Quanto mais detalhada e rica a descrição, melhor a IA acerta.
        </p>
        <table className="guide-table">
          <thead><tr><th>Descrição</th><th>Qualidade</th></tr></thead>
          <tbody>
            <tr><td>"agendar"</td><td><span className="guide-tag guide-tag--red">Ruim</span> — muito vaga</td></tr>
            <tr><td>"O cliente quer agendar"</td><td><span className="guide-tag guide-tag--amber">Razoável</span></td></tr>
            <tr><td>"O cliente deseja agendar, remarcar ou cancelar um compromisso, consulta ou atendimento. Inclui menções a datas, horários, agenda ou marcar horário."</td><td><span className="guide-tag guide-tag--green">Ótima</span> — detalhada e completa</td></tr>
          </tbody>
        </table>

        <div className="guide-callout guide-callout--info">
          <IconBulb size={16} />
          <div>
            <strong>Dica:</strong> O Wizard de criação já preenche as descrições automaticamente
            quando cria o agente. Se você editar depois, mantenha as descrições detalhadas.
          </div>
        </div>

        <h4>Confiança mínima no Roteador</h4>
        <table className="guide-table">
          <thead><tr><th>Valor</th><th>Comportamento</th><th>Recomendação</th></tr></thead>
          <tbody>
            <tr><td><strong>40-50%</strong></td><td>Mais agressivo — tenta rotear mesmo com pouca certeza</td><td>Quando é melhor tentar do que ir ao FAQ</td></tr>
            <tr><td><strong>60%</strong></td><td>Equilibrado (padrão)</td><td>Caso geral — recomendado</td></tr>
            <tr><td><strong>70-80%</strong></td><td>Conservador — só roteia com boa certeza</td><td>Quando as rotas levam a ações importantes</td></tr>
          </tbody>
        </table>

        <div className="guide-callout guide-callout--warning">
          <IconAlertTriangle size={16} />
          <div>
            <strong>Sempre conecte o Fallback!</strong> Se nenhuma rota corresponder à mensagem do cliente,
            o fluxo segue pela saída Fallback. Se ela não estiver conectada, o cliente fica sem resposta.
            O ideal é conectar o Fallback ao nó de FAQ.
          </div>
        </div>

        <div className="guide-callout guide-callout--success">
          <IconCircleCheck size={16} />
          <div>
            <strong>Quando usar o Roteador vs. Condições encadeadas?</strong><br />
            Use o <strong>Roteador</strong> quando o agente precisa direcionar entre 2 ou mais capacidades diferentes (é mais limpo e eficiente — uma única chamada à IA).<br />
            Use <strong>Condições simples</strong> para verificações pontuais como horário, dados coletados, ou uma única intenção binária.
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'problems',
    title: '9. Problemas Comuns',
    content: (
      <>
        <div className="guide-problem">
          <h4>"O agente disse que não tem a informação, mas tem na base"</h4>
          <ul>
            <li>Verifique se a informação está realmente na base (retreine se preciso)</li>
            <li>Use termos parecidos com os que estão na base</li>
            <li>Adicione sinônimos na descrição do produto</li>
            <li>Verifique se não é um PDF escaneado</li>
          </ul>
        </div>

        <div className="guide-problem">
          <h4>"O agente enviou fotos do produto errado"</h4>
          <ul>
            <li>Coloque TODAS as informações do produto na <strong>mesma linha</strong> da planilha</li>
            <li>Verifique se não há linhas com informações parciais</li>
          </ul>
        </div>

        <div className="guide-problem">
          <h4>"O agente repete a saudação toda vez"</h4>
          <ul>
            <li>O nó de saudação deve estar <strong>antes</strong> do loop, não dentro</li>
            <li>Dentro do loop, use uma mensagem de fallback diferente</li>
          </ul>
        </div>

        <div className="guide-problem">
          <h4>"A resposta do FAQ sumiu"</h4>
          <ul>
            <li>O FAQ é não-interativo — responde uma vez e avança</li>
            <li>Crie um <strong>loop</strong>: FAQ → Condição → Fallback → volta ao FAQ</li>
          </ul>
        </div>

        <div className="guide-problem">
          <h4>"O fluxo para e o cliente fica sem resposta"</h4>
          <ul>
            <li>Uma condição tem o lado "Não" ou "Sim" sem conexão</li>
            <li>Sempre conecte <strong>ambos os lados</strong> de cada condição</li>
          </ul>
        </div>
      </>
    ),
  },
  {
    id: 'best-practices',
    title: '10. Boas Práticas',
    content: (
      <>
        <h4>Estrutura do fluxo</h4>
        <ul>
          <li><strong>Sempre conecte os dois lados da condição</strong> — nunca deixe uma saída solta</li>
          <li><strong>Coloque a saudação fora do loop</strong> — o nó de boas-vindas deve executar só uma vez</li>
          <li><strong>Mantenha uma ação de fallback</strong> — no final de cada caminho "Não"</li>
          <li><strong>Use o loop FAQ → Condição → Fallback → FAQ</strong> — é o padrão mais robusto</li>
        </ul>

        <h4>Condições e Roteador</h4>
        <ul>
          <li>Use o <strong>Roteador Inteligente</strong> para direcionar entre múltiplas capacidades — é mais limpo que encadear condições</li>
          <li>Use confiança entre <strong>70-80%</strong> para condições simples, <strong>60%</strong> para o roteador</li>
          <li>Use confiança alta (<strong>85-95%</strong>) para ações irreversíveis</li>
          <li>Escreva descrições semânticas <strong>detalhadas</strong> nas rotas — quanto mais rica, melhor a IA acerta</li>
          <li>Sempre conecte o <strong>Fallback</strong> do roteador (geralmente ao FAQ)</li>
        </ul>

        <h4>Base de conhecimento</h4>
        <ul>
          <li><strong>Planilhas:</strong> uma entidade por linha, colunas claras e descritivas</li>
          <li><strong>PDFs:</strong> prefira texto selecionável, não escaneado</li>
          <li><strong>Retreine</strong> sempre após atualizar arquivos na base</li>
        </ul>

        <h4>Organização geral</h4>
        <ul>
          <li><strong>Comece simples</strong> — faça um fluxo básico funcionar antes de adicionar complexidade</li>
          <li><strong>Teste cada caminho</strong> — simule cada intenção para verificar o fluxo</li>
          <li><strong>Nomeie seus nós</strong> — nomes descritivos facilitam a manutenção</li>
        </ul>
      </>
    ),
  },
];

// ===============================================
// ACCORDION SECTION
// ===============================================

const GuideSection = ({ section, isOpen, onToggle }) => {
  return (
    <div className={`guide-section ${isOpen ? 'guide-section--open' : ''}`}>
      <button className="guide-section__header" onClick={onToggle}>
        <span className="guide-section__title">{section.title}</span>
        {isOpen ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />}
      </button>
      {isOpen && (
        <div className="guide-section__content">
          {section.content}
        </div>
      )}
    </div>
  );
};

// ===============================================
// MAIN MODAL
// ===============================================

const UserGuideModal = ({ show, onHide }) => {
  const [openSections, setOpenSections] = useState(['what-is']);

  const toggleSection = (id) => {
    setOpenSections((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const expandAll = () => setOpenSections(SECTIONS.map((s) => s.id));
  const collapseAll = () => setOpenSections([]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      className="user-guide-modal"
      scrollable
    >
      <Modal.Header closeButton className="user-guide-modal__header">
        <Modal.Title className="user-guide-modal__title">
          <div className="user-guide-modal__title-icon">
            <IconBook2 size={22} />
          </div>
          Guia do Fluxo de Agentes
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="user-guide-modal__body">
        <p className="user-guide-modal__intro">
          Este guia explica como criar e configurar fluxos de conversação para seus agentes de atendimento.
          Clique em cada seção para expandir.
        </p>

        <div className="user-guide-modal__controls">
          <button className="user-guide-modal__control-btn" onClick={expandAll}>
            Expandir tudo
          </button>
          <button className="user-guide-modal__control-btn" onClick={collapseAll}>
            Recolher tudo
          </button>
        </div>

        <div className="guide-sections">
          {SECTIONS.map((section) => (
            <GuideSection
              key={section.id}
              section={section}
              isOpen={openSections.includes(section.id)}
              onToggle={() => toggleSection(section.id)}
            />
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer className="user-guide-modal__footer">
        <Button variant="primary" size="sm" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UserGuideModal;
